import React, { useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  Pagination,
  Toolbar,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { membershipService } from '../../services/membershipService';
import { Subscription } from '../../types/membership';
import CreateSubscriptionDialog from './CreateSubscriptionDialog';
import CashPaymentDialog from './CashPaymentDialog';

const SubscriptionsTab: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState<'renew' | 'upgrade' | 'pause' | 'cancel' | 'extend'>('renew');
  const [actionData, setActionData] = useState<any>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const queryClient = useQueryClient();

  const { data: subscriptionsData, isLoading } = useQuery({
    queryKey: ['subscriptions', page, searchTerm, statusFilter],
    queryFn: () => membershipService.getSubscriptions({
      page,
      limit: 10,
      search: searchTerm || undefined,
      status: statusFilter || undefined,
    }),
  });

  const subscriptionActionMutation = useMutation({
    mutationFn: async ({ id, action, data }: { id: string; action: string; data?: any }) => {
      switch (action) {
        case 'activate':
          return membershipService.activateSubscription(id);
        case 'renew':
          return membershipService.renewSubscription(id, data);
        case 'upgrade':
          return membershipService.upgradeSubscription(id, data);
        case 'pause':
          return membershipService.pauseSubscription(id, data);
        case 'resume':
          return membershipService.resumeSubscription(id);
        case 'cancel':
          return membershipService.cancelSubscription(id, data);
        case 'extend':
          return membershipService.extendSubscription(id, data);
        default:
          throw new Error('Invalid action');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
      setActionDialogOpen(false);
      setAnchorEl(null);
      setSelectedSubscription(null);
      setSnackbar({ open: true, message: 'Subscription updated successfully', severity: 'success' });
    },
    onError: (error: any) => {
      setSnackbar({ open: true, message: error.message || 'Failed to update subscription', severity: 'error' });
    },
  });

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, subscription: Subscription) => {
    setAnchorEl(event.currentTarget);
    setSelectedSubscription(subscription);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSubscription(null);
  };

  const handleAction = (action: string, data?: any) => {
    if (selectedSubscription) {
      if (action === 'payment') {
        setPaymentDialogOpen(true);
      } else if (['renew', 'upgrade', 'pause', 'cancel', 'extend'].includes(action)) {
        setActionType(action as any);
        setActionData(data || {});
        setActionDialogOpen(true);
      } else {
        subscriptionActionMutation.mutate({ id: selectedSubscription.id, action, data });
      }
    }
    handleMenuClose();
  };

  const handleActionSubmit = () => {
    if (selectedSubscription) {
      subscriptionActionMutation.mutate({
        id: selectedSubscription.id,
        action: actionType,
        data: actionData,
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE': return 'success';
      case 'PENDING': return 'warning';
      case 'PAUSED': return 'info';
      case 'CANCELLED': return 'error';
      case 'EXPIRED': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(price);
  };

  const subscriptions = (subscriptionsData as any)?.data?.subscriptions || (subscriptionsData as any)?.subscriptions || [];
  const totalPages = (subscriptionsData as any)?.data?.totalPages || (subscriptionsData as any)?.totalPages || 1;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Subscriptions</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Create Subscription
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ mb: 3 }}>
        <Toolbar sx={{ gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search subscriptions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
            sx={{ minWidth: 250 }}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="PAUSED">Paused</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
              <MenuItem value="EXPIRED">Expired</MenuItem>
            </Select>
          </FormControl>
        </Toolbar>
      </Paper>

      {/* Subscriptions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Customer</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>End Date</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subscriptions.map((subscription: Subscription) => (
              <TableRow key={subscription.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {subscription.customer.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {subscription.customer.phone}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {subscription.membershipPlan.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {subscription.membershipPlan.type.replace('_', ' ')}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={subscription.status}
                    color={getStatusColor(subscription.status) as any}
                    size="small"
                  />
                </TableCell>
                <TableCell>{formatDate(subscription.startDate)}</TableCell>
                <TableCell>{formatDate(subscription.endDate)}</TableCell>
                <TableCell>{formatPrice(subscription.membershipPlan.price)}</TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuClick(e, subscription)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => setPage(newPage)}
          />
        </Box>
      )}

      {/* Empty State */}
      {subscriptions.length === 0 && !isLoading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No subscriptions found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first subscription to get started
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialogOpen(true)}>
            Create Subscription
          </Button>
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedSubscription?.status === 'PENDING' && (
          <MenuItem onClick={() => handleAction('activate')}>Activate</MenuItem>
        )}
        {selectedSubscription?.status === 'ACTIVE' && [
          <MenuItem key="renew" onClick={() => handleAction('renew')}>Renew</MenuItem>,
          <MenuItem key="upgrade" onClick={() => handleAction('upgrade')}>Upgrade</MenuItem>,
          <MenuItem key="pause" onClick={() => handleAction('pause')}>Pause</MenuItem>,
          <MenuItem key="extend" onClick={() => handleAction('extend')}>Extend</MenuItem>,
        ]}
        {selectedSubscription?.status === 'PAUSED' && (
          <MenuItem onClick={() => handleAction('resume')}>Resume</MenuItem>
        )}
        {['ACTIVE', 'PAUSED'].includes(selectedSubscription?.status || '') && (
          <MenuItem onClick={() => handleAction('cancel')}>Cancel</MenuItem>
        )}
        <MenuItem onClick={() => handleAction('payment')}>Record Payment</MenuItem>
      </Menu>

      {/* Create Subscription Dialog */}
      <CreateSubscriptionDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
          setSnackbar({ open: true, message: 'Subscription created successfully', severity: 'success' });
        }}
      />

      {/* Cash Payment Dialog */}
      <CashPaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        subscription={selectedSubscription}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['subscriptions'] });
          setSnackbar({ open: true, message: 'Payment recorded successfully', severity: 'success' });
        }}
      />

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)}>
        <DialogTitle>
          {actionType.charAt(0).toUpperCase() + actionType.slice(1)} Subscription
        </DialogTitle>
        <DialogContent>
          {actionType === 'renew' && (
            <TextField
              fullWidth
              label="New End Date"
              type="date"
              value={actionData.endDate || ''}
              onChange={(e) => setActionData({ ...actionData, endDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 2 }}
            />
          )}
          {actionType === 'extend' && (
            <TextField
              fullWidth
              label="Extend by (days)"
              type="number"
              value={actionData.days || ''}
              onChange={(e) => setActionData({ ...actionData, days: Number(e.target.value) })}
              sx={{ mt: 2 }}
            />
          )}
          {(actionType === 'pause' || actionType === 'cancel') && (
            <TextField
              fullWidth
              label="Reason (optional)"
              multiline
              rows={3}
              value={actionData.reason || ''}
              onChange={(e) => setActionData({ ...actionData, reason: e.target.value })}
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleActionSubmit}
            variant="contained"
            disabled={subscriptionActionMutation.isPending}
          >
            {subscriptionActionMutation.isPending ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SubscriptionsTab;